// Use Web Crypto API for browser compatibility
import { defineStore } from 'pinia';
import * as Sentry from '@sentry/nuxt';
import { RouteName } from '@/types/enums/routes.enum';
import { NotificationType } from '~/types/enums/notification.enum';

const EXPIRATION_BUFFER_SECONDS = 120; // refresh token 2 minutes before expiration
const PREFIX = 'greenbow.api.';
interface InitializeParams {
  cookie?: string;
}
const storage = useStorage();

interface User {
  userId: string;
  firstname: string;
  lastname: string;
  roles: string[];
  partnerId: string | null;
  customerNumber: string;
}

interface AuthState {
  deviceId?: string;
  credential?: string;
  loginRequestId?: string;
  loginRequestExpiration?: string;
  accessToken?: string;
  refreshToken?: string;
  refreshingPromise?: Promise<void> | null;
  isRefreshing: boolean;
  initialized: boolean;
  rateLimitedUntil?: number;
  rateLimitType?: 'login_attempts' | 'ip_attempts';
}

function getDefaultState(): AuthState {
  return {
    deviceId: undefined,
    credential: undefined,
    loginRequestId: undefined,
    loginRequestExpiration: undefined,
    accessToken: undefined,
    refreshToken: undefined,
    refreshingPromise: null,
    isRefreshing: false,
    initialized: false,
    rateLimitedUntil: undefined,
    rateLimitType: undefined
    // Add other initial state properties here
  };
}

export const useAuthStore = defineStore('Authentication', {
  state: (): AuthState => getDefaultState(),
  getters: {
    user(): User | null {
      if (!this.accessToken) {
        return null;
      }
      return decodeToken(this.accessToken);
    },
    isLoggedIn(): boolean {
      return !!this.user;
    },
    isPartner(): boolean {
      return this.user?.roles?.includes('PARTNER') || false;
    },
    isCustomer(): boolean {
      return this.user?.roles?.includes('CUSTOMER') || false;
    },
    isCustomerService(): boolean {
      return this.user?.roles?.includes('CUSTOMER_SERVICE') || false;
    },
    isAdmin(): boolean {
      return this.user?.roles?.includes('ADMIN') || false;
    },
    isTokenExpired(): boolean {
      if (!this.accessToken) {
        return true;
      }
      const accessTokenExpiration = new Date(
        decodeToken(this.accessToken).exp * 1000
      );
      const now = useNow();
      return now.value > accessTokenExpiration;
    },
    isRateLimited(): boolean {
      if (!this.rateLimitedUntil) return false;
      return Date.now() < this.rateLimitedUntil;
    },
    rateLimitTimeRemaining(): number {
      if (!this.rateLimitedUntil) return 0;
      const remaining = Math.ceil((this.rateLimitedUntil - Date.now()) / 1000);
      return Math.max(0, remaining);
    }
  },
  actions: {
    async initialize({ cookie }: InitializeParams) {
      const { $logInfo } = useNuxtApp();
      $logInfo('Initializing authentication...', 'Auth Store');
      if (this.initialized) {
        $logInfo('Authentication already initialized.', 'Auth Store');
        return;
      }

      let authData;

      // Server-side: Try to get authentication data from cookies
      if (process.server) {
        $logInfo('Server-side cookie:', 'Auth Store', cookie);
        authData = parseCookie(cookie, 'Authentication');
        $logInfo('Parsed authData from cookie:', 'Auth Store', authData);
      }

      // Client-side: If no authData from cookies, try local storage
      if (!authData) {
        authData = {
          deviceId: await storage.getItem(PREFIX + 'deviceId'),
          refreshToken: await storage.getItem(PREFIX + 'refreshToken'),
          accessToken: await storage.getItem(PREFIX + 'accessToken')
        };
        $logInfo('Auth data from local storage:', 'Auth Store', authData);
      }

      // Assign the values to your state properties
      this.deviceId = authData.deviceId || undefined;
      this.refreshToken = authData.refreshToken || undefined;
      this.accessToken = authData.accessToken || undefined;
      this.initialized = true;

      $logInfo('Authentication initialized.', 'Auth Store');
    },

    async logout() {
      const { $logInfo, $logError } = useNuxtApp();
      $logInfo('Logging out...', 'Auth Store');
      const refreshToken = (await storage.getItem(
        PREFIX + 'refreshToken'
      )) as string;
      const notifyStore = useNotifyStore();
      const runtime = useRuntimeConfig();
      if (refreshToken) {
        try {
          const refreshTokenId = decodeToken(refreshToken).jti;

          // revoke with standard fetch method, NOT the auto-refresh fetch method.
          const response = await fetch(
            `${runtime.public.authService}/tokens/${refreshTokenId}/revoke`,
            {
              method: 'POST',
              headers: {
                authorization: `Bearer ${await storage.getItem(PREFIX + 'accessToken')}`
              }
            }
          );
          if (response.status !== 200) {
            $logInfo(
              'Token revocation failed, but continuing with logout',
              'Auth Store'
            );
          }
        } catch (error: any) {
          $logError(
            error as Error,
            'Auth Store',
            'Error during token revocation (continuing with logout):'
          );
        }
      }
      await storage.removeItem(PREFIX + 'accessToken');
      await storage.removeItem(PREFIX + 'refreshToken');
      await storage.removeItem(PREFIX + 'credential');
      await storage.removeItem(PREFIX + 'loginRequestId');
      await storage.removeItem(PREFIX + 'loginRequestExpiration');
      this.deviceId = undefined;
      this.credential = undefined;
      this.loginRequestId = undefined;
      this.loginRequestExpiration = undefined;
      this.refreshToken = undefined;
      this.accessToken = undefined;
      const { $i18n } = useNuxtApp();
      notifyStore.notify(
        $i18n.t('Auth.success.loggedOut'),
        '',
        NotificationType.Success
      );
      const localeRoute = useLocaleRoute();

      // Reset PostHog identity
      const { $posthog } = useNuxtApp();
      $posthog?.()?.reset();

      const route = localeRoute(RouteName.LOGIN);
      if (route) {
        $logInfo('Navigating to login route after logout.', 'Auth Store');
        await navigateTo(route.fullPath);
      }
      return true;
    },
    async createLogin(credential: string) {
      const { $logInfo, $logError } = useNuxtApp();
      $logInfo('Creating login...', 'Auth Store');
      const notifyStore = useNotifyStore();
      const runtime = useRuntimeConfig();

      try {
        const deviceId = import.meta.client
          ? crypto.randomUUID()
          : 'server-' + Math.random().toString(36).substring(2, 15);
        await storage.setItem(PREFIX + 'deviceId', deviceId);

        // Determine login method based on the credential
        const loginMethod = credential.includes('@') ? 'email' : 'phone';
        await storage.setItem(PREFIX + 'loginMethod', loginMethod);

        const response = await httpPost({
          path: `${runtime.public.authService}/login`,
          payload: {
            credential,
            deviceId
          }
        });

        const { id, expiration } = await response.json();
        await storage.setItem(PREFIX + 'credential', credential);
        await storage.setItem(PREFIX + 'loginRequestId', id);
        await storage.setItem(PREFIX + 'loginRequestExpiration', expiration);
        $logInfo('Login creation successful:', 'Auth Store', {
          id,
          expiration
        });
        return true;
      } catch (err) {
        const { $i18n } = useNuxtApp();
        notifyStore.notify(
          $i18n.t('Auth.error.invalidCredentials'),
          err,
          NotificationType.Error
        );
        $logError(err as Error, 'Auth Store', 'Error creating login:');
        return false;
      }
    },
    async confirmLogin(nonce: string): Promise<{
      success: boolean;
      isRateLimited?: boolean;
      rateLimitData?: any;
    }> {
      const { $logInfo, $logError } = useNuxtApp();
      $logInfo('Confirming login...', 'Auth Store');
      const notifyStore = useNotifyStore();

      try {
        // Use our rate-limited API endpoint instead of external service directly
        const response = await $fetch<{
          accessToken: string;
          refreshToken: string;
        }>('/api/auth/confirm-login', {
          method: 'POST',
          body: {
            loginRequestId: await storage.getItem(PREFIX + 'loginRequestId'),
            deviceId: await storage.getItem(PREFIX + 'deviceId'),
            nonce
          }
        });

        const { accessToken, refreshToken } = response;
        await storage.removeItem(PREFIX + 'loginRequestExpiration');
        await storage.removeItem(PREFIX + 'loginRequestId');
        $logInfo('Login Confirmed:', 'Auth Store', {
          accessToken: accessToken ? 'present' : 'missing',
          refreshToken: refreshToken ? 'present' : 'missing'
        });
        saveTokens({ accessToken, refreshToken });

        // Identify user in Sentry after successful login
        if (this.user) {
          Sentry.setUser({
            id: this.user.userId,
            email: this.credential || undefined
          });

          // Identify user in PostHog — GDPR: no name/email, only non-PII attributes
          const { $posthog } = useNuxtApp();
          const ph = $posthog?.();
          if (ph) {
            ph.identify(this.user.userId, {
              roles: this.user.roles,
              customerNumber: this.user.customerNumber
            });
          }
        }

        const { $i18n } = useNuxtApp();
        notifyStore.notify(
          $i18n.t('Auth.success.loggedIn'),
          '',
          NotificationType.Success
        );
        return { success: true };
      } catch (err: any) {
        // Handle rate limiting specifically
        if (err.status === 429 || err.statusCode === 429) {
          const retryAfter = err.data?.retryAfter || 60;
          const type = err.data?.type || 'login_attempts';

          // Set global rate limit state
          this.rateLimitedUntil = Date.now() + retryAfter * 1000;
          this.rateLimitType = type;

          // Don't show notification here - let UI handle it
          return {
            success: false,
            isRateLimited: true,
            rateLimitData: {
              retryAfter,
              type,
              message: err.data?.message
            }
          };
        }

        // Handle other specific errors
        const { $i18n } = useNuxtApp();
        if (err.status === 400 || err.statusCode === 400) {
          notifyStore.notify(
            $i18n.t('Auth.error.invalidVerificationCode'),
            $i18n.t('Auth.error.verificationCodeSubtext'),
            NotificationType.Error
          );
          return { success: false };
        }

        if (err.status === 404 || err.statusCode === 404) {
          notifyStore.notify(
            $i18n.t('Auth.error.loginExpired'),
            $i18n.t('Auth.error.loginExpiredSubtext'),
            NotificationType.Error
          );
          return { success: false };
        }

        // Generic error
        notifyStore.notify(
          $i18n.t('Auth.error.loginFailed'),
          $i18n.t('Auth.error.loginFailedSubtext'),
          NotificationType.Error
        );
        $logError(err as Error, 'Auth Store', 'Error confirming login:');
        return { success: false };
      }
    },

    async checkForRefresh(): Promise<void> {
      const { $logInfo, $logError } = useNuxtApp();
      $logInfo('Checking for token refresh...', 'Auth Store');
      const notifyStore = useNotifyStore();
      const runtime = useRuntimeConfig();

      this.isRefreshing = true;
      try {
        if (!this.accessToken || !this.refreshToken) {
          throw new Error('Ingen tokens');
        }
        const accessTokenExpiration = decodeToken(this.accessToken).exp;
        const refreshTokenExpiration = decodeToken(this.refreshToken).exp;
        const now = Math.floor(Date.now() / 1000 + 0.5);

        $logInfo('Token expirations:', 'Auth Store', {
          accessTokenExpiration,
          refreshTokenExpiration,
          now
        });

        if (accessTokenExpiration < now && refreshTokenExpiration < now) {
          $logInfo('Both tokens expired, logging out.', 'Auth Store');
          await this.logout();
        } else if (accessTokenExpiration - now < EXPIRATION_BUFFER_SECONDS) {
          if (runtime.public.isDev) {
            notifyStore.notify(
              'Refreshing token',
              accessTokenExpiration - now,
              NotificationType.Info
            );
          }
          $logInfo(
            'Access token nearing expiration, refreshing token.',
            'Auth Store'
          );
          await this.refreshLogin();
        }
        this.isRefreshing = false;
      } catch (err) {
        notifyStore.notify(
          'Kunne ikke tjekke for opdatering af token',
          err,
          NotificationType.Error
        );
        $logError(err as Error, 'Auth Store', 'Error checking for refresh:');
        this.isRefreshing = false;
        throw err;
      }
    },
    async refreshLogin() {
      const { $logInfo, $logError } = useNuxtApp();
      $logInfo('Refreshing login...', 'Auth Store');
      const notifyStore = useNotifyStore();
      const runtime = useRuntimeConfig();

      try {
        const response = await httpPost({
          path: `${runtime.public.authService}/tokens/refresh`,
          payload: {
            refreshToken: this.refreshToken,
            deviceId: this.deviceId
          }
        });
        const { accessToken, refreshToken } = await response.json();
        $logInfo('Login refreshed:', 'Auth Store', {
          accessToken,
          refreshToken
        });
        saveTokens({ accessToken, refreshToken });
      } catch (err) {
        const { $i18n } = useNuxtApp();
        notifyStore.notify(
          $i18n.t('Auth.info.loggingOut'),
          err,
          NotificationType.Error
        );
        $logError(err as Error, 'Auth Store', 'Error refreshing login:');
        await this.logout();
      }
    },

    async resendLogin() {
      const { $logInfo, $logError } = useNuxtApp();
      $logInfo('Resending login code...', 'Auth Store');
      const notifyStore = useNotifyStore();

      try {
        const credential = (await storage.getItem(
          PREFIX + 'credential'
        )) as string;
        if (!credential) {
          throw new Error('No credential found for resend');
        }

        // Call createLogin again with the same credential
        const success = await this.createLogin(credential);
        if (success) {
          const { $i18n } = useNuxtApp();
          notifyStore.notify(
            $i18n.t('Auth.success.resent'),
            '',
            NotificationType.Success
          );
          return true;
        }
        return false;
      } catch (err) {
        const { $i18n } = useNuxtApp();
        notifyStore.notify(
          $i18n.t('Auth.error.resendFailed'),
          $i18n.t('Auth.error.resendFailedSubtext'),
          NotificationType.Error
        );
        $logError(err as Error, 'Auth Store', 'Error resending login code:');
        return false;
      }
    },

    async getCredentialDisplay(): Promise<string> {
      const credential = (await storage.getItem(
        PREFIX + 'credential'
      )) as string;
      if (!credential) {
        return '';
      }

      // Check if it's an email
      if (credential.includes('@')) {
        const [localPart, domain] = credential.split('@');
        if (localPart.length <= 2) {
          return credential; // Don't mask very short emails
        }
        return `${localPart.charAt(0)}***${localPart.slice(-1)}@${domain}`;
      }

      // Assume it's a phone number
      if (credential.length <= 4) {
        return credential; // Don't mask very short numbers
      }
      return `***${credential.slice(-4)}`;
    },
    async authFetch(url: string, options?: any) {
      const { $logInfo } = useNuxtApp();
      $logInfo('Performing authenticated fetch for:', 'Auth Store', url);
      if (!this.isRefreshing) {
        this.refreshingPromise = this.checkForRefresh();
      }
      await this.refreshingPromise;

      if (!options) {
        options = {};
      }
      if (!options.headers) {
        options.headers = {};
      }
      if (!options.headers['content-type']) {
        options.headers['content-type'] = 'application/json';
      }
      options.headers.authorization = `Bearer ${this.accessToken}`;
      return await fetch(url, options);
    },
    checkRateLimit(): {
      isBlocked: boolean;
      timeRemaining: number;
      type?: string;
    } {
      if (!this.rateLimitedUntil) {
        return { isBlocked: false, timeRemaining: 0 };
      }

      const now = Date.now();
      if (now >= this.rateLimitedUntil) {
        // Rate limit expired, clear it
        this.rateLimitedUntil = undefined;
        this.rateLimitType = undefined;
        return { isBlocked: false, timeRemaining: 0 };
      }

      const timeRemaining = Math.ceil((this.rateLimitedUntil - now) / 1000);
      return {
        isBlocked: true,
        timeRemaining,
        type: this.rateLimitType
      };
    },

    resetState() {
      const { $logInfo, $posthog } = useNuxtApp();
      $logInfo('Resetting authentication state...', 'Auth Store');
      Sentry.setUser(null);
      $posthog?.()?.reset();
      this.$state = getDefaultState();
    },

    // Debug method to completely reset login state - only available in development
    debugResetLogin() {
      const runtime = useRuntimeConfig();

      // Only allow in development
      if (runtime.public.isProd) {
        return;
      }

      const { $logInfo } = useNuxtApp();
      $logInfo('DEBUG: Completely resetting login state...', 'Auth Store');

      // Reset rate limiting
      this.rateLimitedUntil = undefined;
      this.rateLimitType = undefined;

      // Reset all auth state
      this.resetState();

      // Clear local storage
      const storage = useStorage();
      storage.removeItem(PREFIX + 'accessToken');
      storage.removeItem(PREFIX + 'refreshToken');
      storage.removeItem(PREFIX + 'credential');
      storage.removeItem(PREFIX + 'loginRequestId');
      storage.removeItem(PREFIX + 'loginRequestExpiration');
      storage.removeItem(PREFIX + 'deviceId');
      storage.removeItem(PREFIX + 'loginMethod');
    }
  },

  persist: true
});

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useAuthStore, import.meta.hot));
}
